import React, { useState } from 'react'
import { AgGridReact } from 'ag-grid-react';
import * as selector from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import { CustomAttributeDefinitionModel } from '../../models/custom-attribute.model';
import CustomAttributeDialog from './custom-attribute-view.component';
import { ColDef } from 'ag-grid-community';

interface Iprops {
    UploadedFile: File;
    JsonFileData: Record<string, string>[];
    MatchedHeaders,
    UnmatchedHeaders,
    HeaderMappingState,
    UnmatchedSelection?,
    UpdateUnMatchedSelection: (unMatchedData: Record<string, string>) => void;
    UpdateHeaderMappingState: (headderMappingStateData: Record<string, string>) => void;
    UpdateMatchedHeaders?: (matchedHeaders: any[]) => void;
    FileColoumnDefs: ColDef[]
}

const DEFAULT_IMPORT_FIELDS = ["FirstName", "LastName", "Email", "PhoneNumber"] as const;

const STANDARD_IMPORT_COLUMN_LABELS: Record<string, string> = {
    FirstName: "First Name",
    LastName: "Last Name",
    PhoneNumber: "Phone Number",
    Email: "Email",
};

const MatchUploadedFileColoumns = (props: Iprops) => {

    const CustomAttributeDefinitions: CustomAttributeDefinitionModel[] = useSelector(selector.customAttributeFields);
    const IsDarkMode: boolean = useSelector(selector.isDarkMode)

    const [rowData, setRowData] = useState<any[]>(props.JsonFileData ?? []);
    const [columnDefs, setColumnDefs] = useState<ColDef[]>(props.FileColoumnDefs ?? []);
    const [matchedHeaders, setMatchedHeaders] = useState<any[]>(props.MatchedHeaders ?? []);
    const [unmatchedHeaders, setUnmatchedHeaders] = useState<string[]>(props.UnmatchedHeaders ?? []);
    const [headerMappingState, setHeaderMappingState] = useState<Record<string, any>>(props.HeaderMappingState ?? {});
    const [unmatchedSelection, setUnmatchedSelection] = useState<Record<string, string>>(props.UnmatchedSelection ?? {});
    const [showCreateAttributeDialog, setShowCreateAttributeDialog] = useState(false);
    const [createAttributeForHeader, setCreateAttributeForHeader] = useState<string | null>(null);

    const toDefaultFieldOptionValue = (fieldKey: string) => `default:${fieldKey}`;

    const getColumnSelectValue = (header: string) => {
        if (header in unmatchedSelection) {
            return unmatchedSelection[header];
        }

        const mapping = headerMappingState[header];
        if (mapping?.type === "default" && mapping.key) {
            return toDefaultFieldOptionValue(mapping.key);
        }
        if (mapping?.type === "custom" && mapping.customColumnDef) {
            return mapping.customColumnDef.Id.toString();
        }
        return "";
    };

    const getColumnMappingStatusLabel = (header: string) => {
        const value = getColumnSelectValue(header);
        if (value === "skip")
            return <span className={`p-1 ml-2 br-2 h5-regular bg-color-con-hist-outbound-msg-bg ${IsDarkMode ? "color-primary-accent" : "color-primary-light"}`}>will skip</span>;
        if (!value) return "\u00A0";
        return <span className={`p-1 ml-2 br-2 h5-regular color-primary-light bg-color-con-hist-outbound-msg-bg ${IsDarkMode ? "color-primary-accent" : "color-primary-light"}`}>Mapped</span>;
    };

    const getAvailableDefaultFields = (header: string) =>
        DEFAULT_IMPORT_FIELDS.filter((fieldKey) => {
            const isUsedElsewhere = Object.entries(headerMappingState).some(
                ([mappedHeader, mapping]) =>
                    mappedHeader !== header &&
                    mapping?.type === "default" &&
                    mapping?.key === fieldKey
            );

            const isSelectedElsewhere = Object.entries(unmatchedSelection).some(
                ([selectedHeader, selectedValue]) =>
                    selectedHeader !== header &&
                    selectedValue === toDefaultFieldOptionValue(fieldKey)
            );

            return !isUsedElsewhere && !isSelectedElsewhere;
        });

    const getAvailableCustomFields = (header: string) =>
        CustomAttributeDefinitions.filter((field) => {
            const isUsedElsewhere = Object.entries(headerMappingState).some(
                ([mappedHeader, mapping]) =>
                    mappedHeader !== header &&
                    mapping?.type === "custom" &&
                    mapping?.customColumnDef?.Id === field.Id
            );

            const isSelectedElsewhere = Object.entries(unmatchedSelection).some(
                ([selectedHeader, selectedValue]) =>
                    selectedHeader !== header &&
                    selectedValue === field.Id.toString()
            );

            return !isUsedElsewhere && !isSelectedElsewhere;
        });

    const updateMatchedHeaderMapping = (header: string, mappedTo: string, type: string) => {
        setMatchedHeaders((prev) => {
            const next = prev.map((matched) =>
                matched.excelHeader === header
                    ? { ...matched, mappedTo, type }
                    : matched
            );
            props.UpdateMatchedHeaders?.(next);
            return next;
        });
    };
    const applyCustomAttributeToHeader = (header: string, selectedField: CustomAttributeDefinitionModel) => {
        if (!selectedField?.Id) return;

        const attributeId = selectedField.Id.toString();
        const nextSelection = { ...unmatchedSelection, [header]: attributeId };
        setUnmatchedSelection(nextSelection);
        props.UpdateUnMatchedSelection(nextSelection);

        const updatedState = {
            ...headerMappingState,
            [header]: {
                type: "custom",
                customColumnDef: selectedField
            }
        };
        setHeaderMappingState(updatedState);
        props.UpdateHeaderMappingState(updatedState);
        updateMatchedHeaderMapping(header, selectedField.Name, "custom");
    };

    const handleAttributeCreated = (attribute: CustomAttributeDefinitionModel) => {
        if (!createAttributeForHeader || !attribute?.Id) return;
        applyCustomAttributeToHeader(createAttributeForHeader, attribute);
        setCreateAttributeForHeader(null);
    };
    // Dropdown change (unmatched and matched columns)
    const handleColumnMappingChange = (header: string, value: string) => {

        // Create New Attribute
        if (value === "create_new") {
            setCreateAttributeForHeader(header);
            setShowCreateAttributeDialog(true);

            const nextSelection = { ...unmatchedSelection, [header]: "" };
            setUnmatchedSelection(nextSelection);
            props.UpdateUnMatchedSelection(nextSelection);
            return;
        }

        // Skip OR clear selection
        if (value === "skip" || value === "") {
            const nextSelection = { ...unmatchedSelection, [header]: value };
            setUnmatchedSelection(nextSelection);
            props.UpdateUnMatchedSelection(nextSelection);

            const updatedState = {
                ...headerMappingState,
                [header]: {
                    type: "unmatched"
                }
            };
            setHeaderMappingState(updatedState);
            props.UpdateHeaderMappingState(updatedState);
            return;
        }

        // Default field selected
        if (value.startsWith("default:")) {
            const defaultKey = value.replace("default:", "");
            const nextSelection = { ...unmatchedSelection, [header]: value };
            setUnmatchedSelection(nextSelection);
            props.UpdateUnMatchedSelection(nextSelection);

            const updatedState = {
                ...headerMappingState,
                [header]: {
                    type: "default",
                    key: defaultKey
                }
            };
            setHeaderMappingState(updatedState);
            props.UpdateHeaderMappingState(updatedState);
            updateMatchedHeaderMapping(header, defaultKey, "default");
            return;
        }

        // Existing Custom Attribute selected
        const selectedField = CustomAttributeDefinitions.find(
            (x) => x.Id.toString() === value
        );
        if (selectedField) {
            applyCustomAttributeToHeader(header, selectedField);
        }
    };

    const renderColumnMappingOptions = (header: string) => {
        const availableDefaultFields = getAvailableDefaultFields(header);
        const availableCustomFields = getAvailableCustomFields(header);

        return (
            <>
                <option value="">Select column</option>

                {availableDefaultFields.map((fieldKey) => (
                    <option key={fieldKey} value={toDefaultFieldOptionValue(fieldKey)}>
                        {STANDARD_IMPORT_COLUMN_LABELS[fieldKey] ?? fieldKey}
                    </option>
                ))}

                {availableCustomFields.map((field) => (
                    <option key={field.Id} value={field.Id}>
                        {field.Name}
                    </option>
                ))}

                <option value="skip">Skip this field</option>

                <option value="create_new">Create New Attribute</option>
            </>
        );
    };

    const isColumnPending = (header: string) => {
        const value = getColumnSelectValue(header);
        // Only "Select column" (empty) is pending. Skip and mapped fields are elected/resolved.
        return !value;
    };

    const totalColumns = (matchedHeaders?.length ?? 0) + (unmatchedHeaders?.length ?? 0);
    const allColumnHeaders = [
        ...(matchedHeaders ?? []).map((matched) => matched.excelHeader),
        ...(unmatchedHeaders ?? []),
    ];
    const pendingAttentionCount = allColumnHeaders.filter(isColumnPending).length;
    const selectedColumnCount = totalColumns - pendingAttentionCount;
    const allColumnsResolved = pendingAttentionCount === 0;

    return (
        <div>
            <div className={`w-p-100 ${allColumnsResolved ? "bg-color-secure-file-active" : IsDarkMode ? "bg-color-import-contacts-outlook-background" : "bg-color-dashboard-box"}  br-2 py-1 mb-1`}>
                <div className='w-p-95 mx-auto'>
                    {
                        allColumnsResolved ?
                            <div className='d-flex align-center'>
                                <div className='w-4 h-4 br-50 color-secondary h3-medium bg-color-primary-success d-flex align-center justify-center'> <span>✓</span></div>
                                <div className='color-primary-success ml-1'>
                                    <div className='h5-medium'>All columns matched automatically — nothing to do here.</div>
                                </div>
                            </div>
                            : <div className='d-flex align-center'>
                                <div className='w-4 h-4 br-50 color-secondary h3-medium bg-color-primary-warning d-flex align-center justify-center'> <span>!</span></div>
                                <div className='color-primary-warning ml-1'>
                                    <div className='h5-medium'>{selectedColumnCount} of {totalColumns} columns matched · {pendingAttentionCount} need your attention</div>
                                    <div className='h5-regular'>Decide what to do with each unmapped column below — assign to an existing attribute, create a new one, or skip.</div>
                                </div>
                            </div>
                    }
                </div>

            </div>
            <div className={`w-p-100 bg-color-secondary br-3 py-1`}>
                <div className=' w-p-95 mx-auto'>
                    <div className=''>
                        <div className='color-menu-list-font-subnav h4-medium my-1'>File preview · First 4 rows</div>
                        <div className={`${IsDarkMode ? "ag-theme-alpine-dark" : "ag-theme-alpine"} ag-theme-alpine-1 h-27 border-primary-divider br-4`}
                        >
                            <AgGridReact
                                rowData={rowData?.slice(0, 4)}
                                columnDefs={columnDefs}
                                pagination={false}
                                rowHeight={35}
                                headerHeight={35}
                            />
                        </div>
                        <div className='mt-2 color-menu-list-font-subnav h4-medium d-flex justify-between'>
                            <span>Auto-matched · {matchedHeaders?.length} columns</span>
                            <span className='h5-regular'>Wrong guess? Change any mapping with the dropdown.</span>
                        </div>
                        <div className='h5-regular w-p-100'>
                            {matchedHeaders.map((x, i) => (
                                <div key={i} className='d-flex w-p-100 align-center my-1 h4-regular'>
                                    <span className={`w-p-35 ${IsDarkMode ? "bg-color-primary-background" : "bg-color-report-background"} p-1 br-2 border-primary-divider mr-2`}>{x.excelHeader}</span>→
                                    <select
                                        value={getColumnSelectValue(x.excelHeader)}
                                        onChange={(e) =>
                                            handleColumnMappingChange(x.excelHeader, e.target.value)
                                        }
                                        className='p-1 ml-2 w-p-35 border-primary-divider br-2 bg-color-secure-file-active'
                                    >
                                        {renderColumnMappingOptions(x.excelHeader)}
                                    </select>
                                    {!!getColumnSelectValue(x.excelHeader) && (
                                        <span className='p-1 bg-color-secure-file-active ml-2 br-2'>✓ Matched</span>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className='color-menu-list-font-subnav h4-medium mt-2'>
                            {!!unmatchedHeaders?.length && <span>Needs your attention · {pendingAttentionCount} columns</span>}
                        </div>

                        <div className='h5-regular w-p-100'>
                            {unmatchedHeaders.map((header, i) => (
                                <div key={i} className='d-flex w-p-100 align-center my-1 h4-regular'>
                                    <span className={`w-p-35 ${IsDarkMode ? "bg-color-primary-background" : "bg-color-report-background"} p-1 br-2 border-primary-divider mr-2`}>{header}</span>→

                                    <select
                                        value={getColumnSelectValue(header)}
                                        onChange={(e) =>
                                            handleColumnMappingChange(header, e.target.value)
                                        }
                                        className='p-1 ml-2 w-p-35 border-primary-divider br-2 bg-color-secondary color-primary-accent'
                                    >
                                        {renderColumnMappingOptions(header)}
                                    </select>

                                    {getColumnMappingStatusLabel(header)}
                                </div>
                            ))}
                        </div>
                    </div>
                    {showCreateAttributeDialog &&
                        <CustomAttributeDialog
                            show={showCreateAttributeDialog}
                            onClose={() => {
                                setShowCreateAttributeDialog(false);
                                setCreateAttributeForHeader(null);
                            }}
                            onSave={() => { }}
                            initialValue={undefined}
                            onAttributeSave={handleAttributeCreated}
                        />
                    }
                </div>
            </div>
        </div>
    );
};

export default MatchUploadedFileColoumns;